import React, { useState, useEffect } from 'react';
import { Users, Shield, CheckCircle, Search, Mail } from 'lucide-react';
import { useTeams } from '@snipit/core/hooks/useTeams';
import { useOrganization } from '@snipit/core/hooks/useOrganization';
import { useAuth } from '@snipit/core/context/AuthContext';

export default function TeamsView() {
  const { teams, currentTeam: initialCurrentTeam, joinRequests, addTeamMember, approveJoinRequest } = useTeams();
  const { orgMember, members } = useOrganization();
  const { currentUser, userLicense } = useAuth();
  const [inviteEmail, setInviteEmail] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState(null);

  useEffect(() => {
    if (initialCurrentTeam && !selectedTeamId) {
      setSelectedTeamId(initialCurrentTeam.id);
    }
  }, [initialCurrentTeam, selectedTeamId]);

  const currentTeam = teams.find(t => t.id === selectedTeamId) || initialCurrentTeam;
  const myTeams = teams.filter(t => orgMember?.teamIds?.includes(t.id));
  const currentTeamMembers = members.filter(m => m.teamIds?.includes(currentTeam?.id));
  const myTeamRole = orgMember?.teamRoles?.[currentTeam?.id] || 'member';


  if (userLicense && userLicense.tier !== 'enterprise_org') {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8 space-y-6">
        <Shield className="w-20 h-20 text-indigo-500" />
        <h2 className="text-3xl font-bold text-white">Access Denied</h2>
        <p className="text-gray-400 max-w-lg text-lg">
          Team Workspaces are exclusive to SnipIT Enterprise Teams.
        </p>
      </div>
    );
  }

  const handleInvite = async () => {
    if (!inviteEmail) return;
    try {
      await addTeamMember(currentTeam.id, inviteEmail, 'viewer');
      alert(`Invited ${inviteEmail}`);
      setInviteEmail('');
    } catch (err) {
      alert("Failed to invite.");
    }
  };

  return (
    <div className="w-full h-full bg-[#121212] text-gray-300 flex flex-col p-6 lg:p-8">
      <div className="flex justify-between items-center mb-6 shrink-0">
        <h1 className="text-white font-bold text-2xl flex items-center gap-2">
          <Users className="text-indigo-500" /> Enterprise Teams
        </h1>
        {myTeams.length > 1 && (
          <select 
            className="bg-[#1a1a1a] text-white border border-gray-800 rounded-lg px-3 py-2 outline-none text-sm cursor-pointer"
            value={selectedTeamId || ''}
            onChange={(e) => setSelectedTeamId(e.target.value)}
          >
            {myTeams.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        )}
      </div>

      <div className="flex flex-col grow gap-6 overflow-y-auto pr-2 custom-scrollbar">
        {!currentTeam ? (
          <div className="bg-[#1a1a1a] p-8 rounded-xl border border-indigo-900/50 shadow-xl flex flex-col items-center justify-center text-center">
            <Users className="text-indigo-500 w-16 h-16 mb-4 opacity-80" />
            <h2 className="text-2xl font-bold text-white mb-2">No Team Selected</h2>
            <p className="text-gray-400 max-w-md mb-6">
              You haven't been assigned to a team yet. Please contact your organization administrator to assign you to a workspace.
            </p>
          </div>
        ) : (
          <>
            <div className="bg-[#1a1a1a] p-6 rounded-xl border border-gray-800 shadow-xl">
              <h3 className="text-lg font-bold text-white mb-4">Team Info: {currentTeam.name}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#121212] p-4 rounded-lg border border-gray-800">
                  <p className="text-sm text-gray-400">Total Members</p>
                  <p className="text-2xl font-bold text-white">{currentTeamMembers.length}</p>
                </div>
                <div className="bg-[#121212] p-4 rounded-lg border border-gray-800">
                  <p className="text-sm text-gray-400">Your Role</p>
                  <p className="text-2xl font-bold text-indigo-400 capitalize">
                    {myTeamRole === 'lead' ? 'Team Lead' : myTeamRole}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#1a1a1a] p-6 rounded-xl border border-gray-800 shadow-xl">
              <h3 className="text-lg font-bold text-white mb-4">Invite Members</h3>
              <div className="flex gap-2">
                <div className="relative grow">
                  <Mail className="absolute left-3 top-3 text-gray-500" size={16} />
                  <input
                    type="email"
                    className="w-full bg-[#121212] p-3 pl-10 text-sm rounded-lg outline-none border border-gray-800 focus:border-indigo-500/50 transition-colors"
                    placeholder="Enter email address..."
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </div>
                <button 
                  onClick={handleInvite}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white px-6 rounded-lg font-bold transition shrink-0"
                >
                  Send Invite
                </button>
              </div>
            </div>

            {joinRequests && joinRequests.length > 0 && (
              <div className="bg-[#1a1a1a] p-6 rounded-xl border border-amber-900/30 shadow-xl">
                <h3 className="text-lg font-bold text-amber-400 mb-4">Pending Requests</h3>
                <div className="space-y-3">
                  {joinRequests.map(req => (
                    <div key={req.id} className="flex justify-between items-center bg-[#121212] p-3 rounded-lg border border-gray-800">
                      <div>
                        <p className="text-sm font-bold text-white">{req.userEmail}</p>
                        <p className="text-xs text-gray-500">Requested on {new Date(req.createdAt).toLocaleDateString()}</p>
                      </div>
                      <button 
                        onClick={() => approveJoinRequest(currentTeam.id, req.id, req.userId, req.userEmail)}
                        className="text-emerald-400 hover:text-emerald-300 bg-emerald-900/20 hover:bg-emerald-900/40 p-2 rounded transition flex items-center gap-1 text-xs font-bold border border-emerald-900/50"
                      >
                        <CheckCircle size={14} /> Approve
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
